import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { AlertCircle } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { useAssignments } from '../hooks/use-assignments'
import { useProductDetail } from '../hooks/use-product-detail'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { assignmentsApi } from '@/api/endpoints/assignments'
import { useRestaurants } from '@/features/products/hooks/use-restaurants'
import { getErrorMessage } from '@/shared/utils/error-codes'
import { ApiError } from '@/api/types'
import { ResponsiveTableWrapper } from '@/shared/components/responsive-table-wrapper'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { ACTIONS } from '@/shared/constants/actions'

interface AssignmentsTabProps {
  sku: string
}

export function AssignmentsTab({ sku }: AssignmentsTabProps) {
  const queryClient = useQueryClient()
  const { t: tProducts } = useTranslation('products')
  const { t: tCommon } = useTranslation('common')
  const { can } = usePermissions()
  const { data: product } = useProductDetail(sku)
  // A merged duplicate (CHG-2026-09-duplicate-product-merge) keeps its assignment rows as-is
  // (frozen), so re-assigning it is disabled — it is the primary that must reach a restaurant.
  const isMerged = !!product?.mergedIntoSku
  const canManage = can(ACTIONS.ASSIGNMENT_MANAGE) && !isMerged
  const {
    data: assignments,
    isLoading: assignmentsLoading,
    error: assignmentsError,
  } = useAssignments(sku)
  const {
    data: restaurants,
    isLoading: restaurantsLoading,
    error: restaurantsError,
  } = useRestaurants()

  const assignMutation = useMutation({
    mutationFn: (restaurantId: number) =>
      assignmentsApi.assign(sku, restaurantId),
    onSuccess: () => {
      toast.success(tProducts('toast.assigned'))
      // Invalidate both assignments and prices (since prices tab shows assigned restaurants)
      queryClient.invalidateQueries({
        queryKey: ['products', sku, 'assignments'],
      })
      queryClient.invalidateQueries({ queryKey: ['products', sku, 'prices'] })
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        toast.error(getErrorMessage(error.status, error.code))
      } else {
        toast.error(tProducts('toast.assignFailed'))
      }
    },
  })

  const unassignMutation = useMutation({
    mutationFn: (restaurantId: number) =>
      assignmentsApi.unassign(sku, restaurantId),
    onSuccess: () => {
      toast.success(tProducts('toast.unassigned'))
      // Invalidate both assignments and prices (since prices tab shows assigned restaurants)
      queryClient.invalidateQueries({
        queryKey: ['products', sku, 'assignments'],
      })
      queryClient.invalidateQueries({ queryKey: ['products', sku, 'prices'] })
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        toast.error(getErrorMessage(error.status, error.code))
      } else {
        toast.error(tProducts('toast.unassignFailed'))
      }
    },
  })

  const handleToggle = (restaurantId: number, isAssigned: boolean) => {
    if (!canManage) return
    if (isAssigned) {
      unassignMutation.mutate(restaurantId)
    } else {
      assignMutation.mutate(restaurantId)
    }
  }

  if (assignmentsLoading || restaurantsLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    )
  }

  if (assignmentsError || restaurantsError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>{tCommon('error')}</AlertTitle>
        <AlertDescription>
          {tProducts('assignments.loadErrorDescription')}
        </AlertDescription>
      </Alert>
    )
  }

  if ((restaurants?.items.length ?? 0) === 0) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>{tProducts('assignments.noRestaurantsTitle')}</AlertTitle>
        <AlertDescription>
          {tProducts('assignments.noRestaurantsDescription')}
        </AlertDescription>
      </Alert>
    )
  }

  const assignedRestaurantIds = new Set(
    assignments?.filter((a) => a.assigned).map((a) => a.restaurantId) || [],
  )

  return (
    <div>
      <p className="text-sm text-muted-foreground mb-4">
        {tProducts('assignments.description')}
      </p>

      <ResponsiveTableWrapper>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{tProducts('assignments.tableRestaurant')}</TableHead>
              <TableHead>{tProducts('assignments.tableCode')}</TableHead>
              <TableHead className="text-right">
                {tProducts('assignments.tableAssigned')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {restaurants?.items.map((restaurant) => {
              const isAssigned = assignedRestaurantIds.has(restaurant.id)
              const isPending =
                (assignMutation.isPending || unassignMutation.isPending) &&
                (assignMutation.variables === restaurant.id ||
                  unassignMutation.variables === restaurant.id)

              return (
                <TableRow key={restaurant.id}>
                  <TableCell className="font-medium">
                    {restaurant.name}
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    {restaurant.code}
                  </TableCell>
                  <TableCell className="text-right">
                    <Switch
                      checked={isAssigned}
                      onCheckedChange={() =>
                        handleToggle(restaurant.id, isAssigned)
                      }
                      disabled={isPending || !canManage}
                    />
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </ResponsiveTableWrapper>
    </div>
  )
}
